import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  Description as DescriptionIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  FolderZip as FolderZipIcon,
  Inventory2 as Inventory2Icon,
  PictureAsPdf as PictureAsPdfIcon,
  School as SchoolIcon,
  Search as SearchIcon,
  ShoppingBag as ShoppingBagIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../../api';
import downloadPaperPdf from '../../../components/scan/downloadPaperPdf';
import BillingPagination, { useBillingPagination } from '../../../components/BillingPagination';

/**
 * Parent → My Purchases → "Purchases": everything bought for the family (practice papers,
 * mock exams, PDF packs, courses) with watermarked downloads of question papers and answers.
 * Parents never need the PIN; that gate is only for students. £0 rows written when a child
 * opened an item under a mobile plan are not purchases: they live in the "Mobile plan" tab.
 */

const TYPES = [
  { key: 'all', label: 'All' },
  { key: 'paper', label: 'Practice papers' },
  { key: 'mock_exam', label: 'Mock exams' },
  { key: 'bundle', label: 'Packs & bundles' },
  { key: 'course', label: 'Courses' },
];

const typeMeta = (type) => {
  switch (type) {
    case 'mock_exam':
      return { label: 'Mock Exam', color: 'secondary', icon: <AssessmentIcon /> };
    case 'course':
      return { label: 'Course', color: 'primary', icon: <SchoolIcon /> };
    case 'bundle':
      return { label: 'Pack', color: 'info', icon: <Inventory2Icon /> };
    default:
      return { label: 'Practice Paper', color: 'default', icon: <DescriptionIcon /> };
  }
};

const progressOf = (row) => {
  if (row.type === 'course' || row.type === 'bundle') return { label: 'Purchased', color: 'success' };
  if (row.status === 2) return { label: 'Completed', color: 'success' };
  if (row.status === 1) return { label: 'In progress', color: 'warning' };
  return { label: 'Not started', color: 'default' };
};

const ukDate = (v) =>
  v ? new Date(v).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const rowKey = (row) => `${row.type || 'paper'}-${row.purchase_id}`;

// The backend category is still named "Past Papers".
const categoryLabel = (c) => (c === 'Past Papers' ? 'Practice Papers' : c);

function DownloadMenu({ row, busy, onPick }) {
  const [anchor, setAnchor] = useState(null);
  const items = row.downloads || [];
  if (!items.length) {
    return (
      <Typography variant="caption" color="text.secondary">
        {row.type === 'course' ? 'Online course' : 'Online only'}
      </Typography>
    );
  }

  const groups = [];
  items.forEach((item) => {
    const name = item.kind === 'zip' ? null : item.group || null;
    let g = groups.find((x) => x.name === name);
    if (!g) {
      g = { name, items: [] };
      groups.push(g);
    }
    g.items.push(item);
  });
  groups.sort((a, b) => (a.name === null) - (b.name === null));

  return (
    <>
      <Button
        variant="contained"
        size="small"
        disabled={busy}
        onClick={(e) => setAnchor(e.currentTarget)}
        startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}
        endIcon={<ExpandMoreIcon />}
        sx={{
          textTransform: 'none',
          fontWeight: 700,
          borderRadius: 999,
          whiteSpace: 'nowrap',
          background: 'linear-gradient(90deg, #3B2A9F 0%, #667eea 100%)',
          boxShadow: 'none',
        }}
      >
        {busy ? 'Preparing…' : 'Download'}
      </Button>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 240, maxHeight: 420, borderRadius: 2 } }}
      >
        {groups.flatMap((g) => [
          g.name ? (
            <ListSubheader key={`h-${g.name}`} sx={{ lineHeight: '32px', fontWeight: 700, color: '#3B2A9F' }}>
              {g.name}
            </ListSubheader>
          ) : null,
          ...g.items.map((item) => (
            <MenuItem
              key={item.key}
              dense
              onClick={() => {
                setAnchor(null);
                onPick(row, item);
              }}
            >
              <ListItemIcon>
                {item.kind === 'zip' ? (
                  <FolderZipIcon fontSize="small" />
                ) : (
                  <PictureAsPdfIcon fontSize="small" sx={{ color: item.kind === 'answers' ? '#2E7D32' : '#D32F2F' }} />
                )}
              </ListItemIcon>
              <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '0.9rem' }} />
            </MenuItem>
          )),
        ])}
      </Menu>
    </>
  );
}

const PurchasesTab = ({ onCount }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [studentFilter, setStudentFilter] = useState('');
  const [search, setSearch] = useState('');
  const [busyRow, setBusyRow] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Paid rows only: that is what can be downloaded.
        const res = await api.get('parent/paper-purchases', { params: { status: 'paid' } });
        const raw = res.data?.data ?? [];
        const list = (Array.isArray(raw) ? raw : raw?.list ?? []).filter((r) => !r.is_access_grant);
        if (!cancelled) {
          setRows(list);
          onCount?.(list.length);
        }
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.message || 'Could not load your purchases.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const students = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      if (r.student_id && r.student_name) map.set(String(r.student_id), r.student_name);
    });
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [rows]);

  const counts = useMemo(() => {
    const c = { all: rows.length };
    rows.forEach((r) => {
      const t = r.type || 'paper';
      c[t] = (c[t] || 0) + 1;
    });
    return c;
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (typeFilter !== 'all' && (r.type || 'paper') !== typeFilter) return false;
      if (studentFilter && String(r.student_id) !== studentFilter) return false;
      if (!q) return true;
      return [r.paper_name, r.student_name, r.paper_category].some((v) => String(v || '').toLowerCase().includes(q));
    });
  }, [rows, typeFilter, studentFilter, search]);

  const { rowsPerPage, handleChangePage, handleChangeRowsPerPage, paginate } = useBillingPagination(
    `${typeFilter}|${studentFilter}|${search}`
  );
  const { page, count, rows: paginatedItems } = paginate(filtered);

  const handlePick = async (row, item) => {
    setBusyRow(rowKey(row));
    try {
      const ext = item.kind === 'zip' ? 'zip' : 'pdf';
      await downloadPaperPdf(
        `parent/purchases/${row.type}/${row.purchase_id}/download/${item.key}`,
        `${row.paper_name || 'download'} - ${item.label}.${ext}`
      );
    } catch (e) {
      toast.error('Could not download this file.');
    } finally {
      setBusyRow(null);
    }
  };

  return (
    <Box>
      <Card
        elevation={0}
        sx={{ p: 2, mb: 2.5, borderRadius: 3, border: '1px solid rgba(102,126,234,0.2)', bgcolor: 'rgba(102,126,234,0.04)' }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {TYPES.filter((t) => t.key === 'all' || counts[t.key]).map((t) => {
              const active = typeFilter === t.key;
              return (
                <Chip
                  key={t.key}
                  label={`${t.label} (${counts[t.key] || 0})`}
                  onClick={() => setTypeFilter(t.key)}
                  sx={{
                    fontWeight: 700,
                    borderRadius: 999,
                    bgcolor: active ? '#3B2A9F' : '#fff',
                    color: active ? '#fff' : '#374151',
                    border: active ? 'none' : '1px solid #e5e7eb',
                    '&:hover': { bgcolor: active ? '#2f2180' : '#f3f4f6' },
                  }}
                />
              );
            })}
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', flex: { xs: '1 1 100%', md: '0 0 auto' } }}>
            {students.length > 1 && (
              <FormControl size="small" sx={{ minWidth: 180, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}>
                <InputLabel>Student</InputLabel>
                <Select
                  value={studentFilter}
                  label="Student"
                  onChange={(e) => setStudentFilter(e.target.value)}
                  sx={{ bgcolor: '#fff' }}
                >
                  <MenuItem value="">All students</MenuItem>
                  {students.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <TextField
              size="small"
              placeholder="Search purchases"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: { sm: 240 }, flex: { xs: '1 1 100%', sm: '0 0 auto' }, '& .MuiOutlinedInput-root': { bgcolor: '#fff' } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Box>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: '1px solid #e5e7eb' }}>
        <Table sx={{ minWidth: 760 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: '#F7F8FD', '& th': { fontWeight: 700, color: '#374151' } }}>
              <TableCell>Item</TableCell>
              <TableCell>Student</TableCell>
              <TableCell>Progress</TableCell>
              <TableCell>Purchased</TableCell>
              <TableCell align="right">Downloads</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 5 }).map((__, j) => (
                    <TableCell key={j}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                  <ShoppingBagIcon sx={{ fontSize: 48, color: '#9ca3af', mb: 1 }} />
                  <Typography color="text.secondary">
                    {rows.length
                      ? 'No purchases match your filters.'
                      : "Nothing bought yet. Items included in your child's mobile plan are under the Mobile plan tab."}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedItems.map((row) => {
                const kind = typeMeta(row.type);
                const progress = progressOf(row);
                const meta = [
                  row.paper_duration_minutes != null ? `${row.paper_duration_minutes} min` : null,
                  row.paper_total_marks != null ? `${row.paper_total_marks} marks` : null,
                ]
                  .filter(Boolean)
                  .join(' · ');
                return (
                  <TableRow key={rowKey(row)} hover>
                    <TableCell sx={{ maxWidth: 480 }}>
                      <Typography sx={{ fontWeight: 600, lineHeight: 1.35 }}>{row.paper_name}</Typography>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: 0.5 }}>
                        <Chip icon={kind.icon} label={kind.label} size="small" color={kind.color} variant="outlined" />
                        {row.paper_category && <Chip label={categoryLabel(row.paper_category)} size="small" />}
                        {meta && (
                          <Typography variant="caption" color="text.secondary">
                            {meta}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color={row.student_name ? 'text.primary' : 'text.secondary'}>
                        {row.student_name || 'Not assigned'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={progress.label}
                        color={progress.color}
                        size="small"
                        variant={progress.color === 'default' ? 'outlined' : 'filled'}
                      />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{ukDate(row.purchased_at)}</TableCell>
                    <TableCell align="right">
                      <DownloadMenu row={row} busy={busyRow === rowKey(row)} onPick={handlePick} />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {!loading && (
        <BillingPagination
          count={count}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
    </Box>
  );
};

export default PurchasesTab;
